import type { GrantablePermission } from '@palaccount/contracts';

/**
 * D26'S LINE BETWEEN A RECORD YOU CAN MAKE IN PLACE AND ONE YOU CANNOT —
 * written ONCE, here, and nowhere else.
 *
 * Delegated ruling D26 (26 Aug 2026): *"create-new pickers → small window,
 * simple records first (customers, suppliers, categories, units); complex
 * forms jump. GEN-51 unblocks."* It adopts the recommendation argued in
 * DECISIONS NEEDED item 26, whose reasoning is the reason this file is a MAP
 * rather than a boolean at each picker:
 *
 *   *"And one answer may not fit all 79. Creating a customer inline is
 *   reasonable; the item form is 1,400 lines and probably is not."*
 *
 * ── WHY A ROSTER AND NOT A FLAG AT THE CALL SITE ───────────────────────────
 *
 * The same entity is picked from many screens — a supplier from six document
 * editors, an item from about fifteen. A per-call-site decision is a decision
 * taken fifteen times, which is fifteen chances to take it differently, and
 * the disagreement would be invisible: two supplier pickers, one opening a
 * window and one navigating away, look identical in a diff and identical in
 * review. Here the entity decides, so every picker pointing at suppliers
 * behaves the same because it is reading the same row.
 *
 * ── THE PART THAT MAKES THE NEXT PICKER LAND CORRECTLY BY CONSTRUCTION ─────
 *
 * `Record<PickerEntity, RosterEntry>` is exhaustive: adding a member to
 * `PickerEntity` without adding its row here is a COMPILE ERROR, not a
 * runtime default. There is deliberately no fallback and no `Partial` — a
 * default would silently pick a side for whoever forgot, and the whole point
 * of D26 is that the side is a judgement somebody has to make. The compiler
 * asks the question; it does not answer it.
 *
 * ── WHAT "SIMPLE" HAD TO MEAN TO BE HONEST ────────────────────────────────
 *
 * Not "few fields on screen" — "creatable through the SAME server contract the
 * full form uses, with no relaxed validation and no invented defaults". Each
 * `window` entry below was checked against its Zod create schema, and each one
 * is genuinely satisfiable from the fields the window shows plus defaults the
 * FULL form already applies. Where that failed, the entity belongs on the
 * `jump` side and the reason is recorded in `why`. There is no third state
 * where a record is created "partly".
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────
 *
 * CURRENCY. The currency picker is not on this roster and must not be added
 * without its owner: currencies are held by a separate stream, and D16 ruled
 * the list open with database-level guarantees behind it. A create-new row on
 * that picker is a decision about that ruling, not about this one.
 *
 * DOCUMENTS. Pickers that choose an existing order, bill, quotation or
 * invoice are not here either. GEN-51 governs a field that picks a RECORD the
 * user could create, and "create a new bill" from inside a payment is not a
 * quick create — it is a different document's whole workflow, and the
 * unsaved-changes warning on the way out is the product telling the truth.
 * They are absent rather than listed as `jump` because listing them would
 * assert a decision nobody has taken.
 */

/**
 * Every entity a "Pick from list" field offers to create today.
 *
 * Customer and supplier are two members and not one `partner`, because they
 * are two different sentences to the reader and two different bodies to the
 * server (`isCustomer` / `isSupplier`), and a picker filtered to suppliers
 * that created a customer would be a defect nobody would see until the record
 * failed to appear in the list it was created from.
 */
export type PickerEntity =
  | 'customer'
  | 'supplier'
  | 'itemCategory'
  | 'unit'
  | 'item'
  | 'account'
  | 'employee'
  | 'warehouse'
  | 'project'
  | 'workOrder';

/**
 * The four of D26's first sentence. A small window over the current screen,
 * the minimal required fields, and the picker takes the new record.
 */
interface WindowEntry {
  readonly mode: 'window';
  /**
   * GEN-51's "if the user's role allows creating that record", as the
   * permission the SERVER demands for the create route — not a near neighbour.
   * A row offered to a role the server will refuse is a worse experience than
   * no row, because the refusal arrives after the typing.
   */
  readonly permission: GrantablePermission;
  /** The window's heading. */
  readonly titleKey: string;
}

/**
 * The complex side. Create-new navigates to the record's own full form, which
 * is what DECISIONS NEEDED item 26 describes and prices honestly:
 *
 *   *"Jumping to the record's own full form — which ABANDONS whatever the
 *   person was half-way through typing, and triggers your unsaved-changes
 *   warning on the way out."*
 *
 * That cost is real and is not hidden here. It is accepted for these entities
 * because the alternative is worse: a window that creates half an item is a
 * record somebody has to go and finish, and nothing on screen would say so.
 */
interface JumpEntry {
  readonly mode: 'jump';
  readonly permission: GrantablePermission;
  /** Where the full form lives, as the router spells it. */
  readonly route: string;
  /**
   * Why this one cannot honestly be made minimal. Prose, in the file, because
   * the next person to look at a `jump` row will be asking exactly this and
   * the answer is otherwise a re-derivation from a Zod schema.
   */
  readonly why: string;
}

export type RosterEntry = WindowEntry | JumpEntry;

export const QUICK_CREATE_ROSTER: Readonly<Record<PickerEntity, RosterEntry>> = {
  /*
   * CreatePartnerRequestSchema requires `name` alone: `isCustomer` defaults
   * true, `isSupplier` false, and every other field is `.optional()`. The
   * window sets the type flag from the picker it was opened from, so a
   * supplier picker makes a supplier. The schema's own `.refine` — "a partner
   * must have at least one type ticked" — is satisfied by construction rather
   * than by hoping.
   */
  customer: { mode: 'window', permission: 'crm.create', titleKey: 'quickCreate.newCustomer' },
  supplier: { mode: 'window', permission: 'crm.create', titleKey: 'quickCreate.newSupplier' },

  /*
   * CreateItemCategoryRequestSchema requires `code` and `name` from the user;
   * everything else is optional except `sortOrder`, which the full form sends
   * as the literal `0`. Two boxes, both shown.
   *
   * A Zod `.default()` is a PARSE-time default, so the generated request TYPE
   * still demands the field — which is why the full form sends it explicitly
   * and why the window does too. Worth knowing before reading a schema and
   * concluding a field is optional: the typechecker caught this, not review.
   */
  itemCategory: {
    mode: 'window',
    permission: 'inventory.create',
    titleKey: 'cats.newTitle',
  },

  /*
   * CreateUnitRequestSchema requires `name`, `symbol` AND `precision` —
   * precision has no Zod default, so this one had to be checked against D26's
   * escape clause ("if there are none and the record can't honestly be created
   * minimal, that entity is complex"). It survives: the FULL form's own
   * `EMPTY` state is `precision: '0'` (`units-page.tsx`), so 0 is the
   * documented default this product already applies to a new unit, not a value
   * invented here. `allowSales` and `allowPurchases` come from the same
   * `EMPTY` (both true) for the same reason — a Zod `.default()` does not make
   * the generated request type's field optional, so the full form states them
   * and so does the window.
   */
  unit: { mode: 'window', permission: 'inventory.create', titleKey: 'units.newTitle' },

  item: {
    mode: 'jump',
    permission: 'inventory.create',
    route: '/inventory/items/new',
    why: 'The item form is the product’s largest, and its required fields are not a subset anybody can type into a small window: an item needs a category and a stock unit, and both of those are themselves pickers. A window that created an item would either ask for the whole form or create a record that cannot be sold until somebody finishes it.',
  },
  account: {
    mode: 'jump',
    permission: 'accounting.create',
    route: '/accounting/chart',
    why: 'A ledger account carries a type, a parent and a place in the chart that decides what it does to every report. There is no honest minimum: an account created with a guessed type is wrong in the trial balance rather than incomplete.',
  },
  employee: {
    mode: 'jump',
    permission: 'hr.create',
    route: '/employees',
    why: 'An employee is a person with a contract, a start date and a pay basis, and D24 has just tied the login lifecycle to that record. Creating one from a timesheet picker would make a half-person that payroll would later find.',
  },
  warehouse: {
    mode: 'jump',
    permission: 'inventory.create',
    route: '/inventory/warehouses',
    why: 'A warehouse is where stock lives; creating one mid-document to receive into is how stock ends up somewhere nobody meant. The full screen is one click away and shows what already exists.',
  },
  project: {
    mode: 'jump',
    permission: 'projects.create',
    route: '/projects',
    why: 'A project carries a customer, a billing basis and dates that govern what its time is worth. None of those has a safe default.',
  },
  workOrder: {
    mode: 'jump',
    permission: 'manufacturing.create',
    route: '/manufacturing/work-orders',
    why: 'A work order is a document with its own lifecycle, not a master record. It is picked here to be referenced, and raising one is that module’s own flow.',
  },
};
